<!-- BaseMessages.vue -->
<template>
  <div 
    v-if="hasInlineMessages" 
    :class="highestSeverityCode" 
    :style="{ maxWidth: maxWidth }"
  >
    <div class="message-layout">
      <div class="message-icon-column">
        <component :is="headingIcon" class="message-icon" size="24" />
      </div>
      <div class="message-content-column">
        <ul class="message-list">
          <li 
            v-for="(message, index) in inlineMessages" 
            :key="index"
            class="message-item"
          >
            <span>{{ message }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Toast Container - teleported to body to escape modal stacking context -->
  <Teleport to="body">
    <div v-if="activeToasts.length > 0" class="toast-container">
      <div
        v-for="toast in activeToasts"
        :key="toast.id"
        :class="['toast', 'success-toast', { 'toast-show': toast.show }]"
      >
        <div class="toast-content">
          <PhCheck class="toast-icon" size="20" />
          <span class="toast-message">{{ toast.message }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { PhWarningCircle, PhWarning, PhCheck, PhInfo } from '@phosphor-icons/vue'

export default {
  name: 'BaseMessages',
  components: {
    PhWarningCircle,
    PhWarning,
    PhCheck,
    PhInfo
  },
  props: {
    messages: {
      type: Array,
      required: false,
      default: () => []
    },
    statusCode: {
      type: String,
      required: false,
      default: 'info',
      validator: value => ['info', 'warning', 'error', 'ok'].includes(value)
    },
    response: {
      type: Object,
      required: false,
      default: () => null
    },
    scrollToTopOnError: {
      type: Boolean,
      required: false,
      default: true
    },
    maxWidth: {
      type: String,
      required: false,
      default: '600px',
      validator: value => {
        // Allow common CSS width values
        return /^(\d+px|\d+%|\d+rem|\d+em|auto|max-content|min-content|fit-content|none)$/.test(value)
      }
    }
  },
  data() {
    return {
      activeToasts: [],
      toastCounter: 0
    }
  },
  computed: {
    // Use response data if available, otherwise use direct props
    finalMessages() {
      if (this.response?.status?.messages) {
        return this.response.status.messages;
      }
      return this.messages || [];
    },
    finalStatusCode() {
      if (this.response?.status?.code) {
        return this.response.status.code;
      }
      return this.statusCode;
    },
    inlineMessages() {
      // Only return messages if there are actual messages AND the status code is not success
      if (this.finalMessages.length === 0) {
        return [];
      }
      return this.finalStatusCode !== 'ok' ? this.finalMessages : []
    },
    successMessages() {
      if (this.finalMessages.length === 0) {
        return [];
      }
      return this.finalStatusCode === 'ok' ? this.finalMessages : []
    },
    hasInlineMessages() {
      return this.inlineMessages.length > 0
    },
    currentCode() {
      return this.finalStatusCode || 'info'
    },
    highestSeverityCode() {
      return {
        'message-container': true,
        'info-container': this.currentCode === 'info',
        'warning-container': this.currentCode === 'warning',
        'error-container': this.currentCode === 'error'
      }
    },
    headingIcon() {
      const icons = {
        info: PhInfo,
        warning: PhWarning,
        error: PhWarningCircle
      }
      return icons[this.currentCode]
    }
  },
watch: {
    successMessages: {
      handler(newMessages) {
        // Show toast for each success message
        newMessages.forEach(message => {
          this.showToast(message);
        });
      },
      immediate: true
    },
    // Watch for inline messages to trigger scroll
    hasInlineMessages: {
      handler(hasMessages) {
        if (hasMessages && this.scrollToTopOnError) {
          this.$nextTick(() => {
            this.scrollToTop();
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    scrollToTop() {     
      // Try to find closest scrollable parent first
      const scrollableParent = this.findScrollableParent(this.$el.parentElement);
      if (scrollableParent) {
        scrollableParent.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        return;
      }
      
      // Fallback to .scrollbar-area, then window
      const scrollbarArea = document.querySelector('.scrollbar-area');
      if (scrollbarArea) {
        scrollbarArea.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    findScrollableParent(element) {
      // Add extra safety checks
      if (!element || !element.parentElement || element === document.body || element === document.documentElement) {
        return null;
      }
      
      try {
        const style = window.getComputedStyle(element);
        const isScrollable = style.overflow === 'auto' || 
                            style.overflow === 'scroll' || 
                            style.overflowY === 'auto' || 
                            style.overflowY === 'scroll';
        
        // Check if element actually has scrollable content
        if (isScrollable && element.scrollHeight > element.clientHeight) {
          return element;
        }
        
        return this.findScrollableParent(element.parentElement);
      } catch (error) {
        console.warn('Error in findScrollableParent:', error);
        return null;
      }
    },
    showToast(message, duration = 4000) {
      const id = ++this.toastCounter;
      const toast = {
        id,
        message,
        show: false
      };
      
      this.activeToasts.push(toast);
      
      // Trigger show animation
      this.$nextTick(() => {
        const toastIndex = this.activeToasts.findIndex(t => t.id === id);
        if (toastIndex !== -1) {
          this.activeToasts[toastIndex].show = true;
        }
      });
      
      // Auto-remove toast
      setTimeout(() => {
        this.removeToast(id);
      }, duration);
    },
    removeToast(id) {
      const toastIndex = this.activeToasts.findIndex(t => t.id === id);
      if (toastIndex !== -1) {
        // Trigger hide animation
        this.activeToasts[toastIndex].show = false;
        
        // Remove from array after animation
        setTimeout(() => {
          this.activeToasts = this.activeToasts.filter(t => t.id !== id);
        }, 300);
      }
    }
  }
}
</script>

<style scoped>
.message-container {
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 20px;
}

.message-layout {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.message-icon-column {
  flex-shrink: 0;
  padding-top: 0.125rem; 
}

.message-icon {
  display: block;
}

.message-content-column {
  flex: 1;
  min-width: 0; 
}

/* Info styling */
.info-container {
  background-color: #f0f9ff;
  border-left-width: 3px;
  border-left-color: #0ea5e9;
}

/* Warning styling */
.warning-container {
  background-color: #fefce8;
}

/* Error styling */
.error-container {
  background-color: #B3001B;
}

.message-item {
  color: var(--background-table);
}

.message-icon {
  color: var(--background-table);
}

.message-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.message-item {
  display: block;
  padding: 0.25rem 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-container {
  animation: slideIn 0.5s ease-out;
}

/* Toast Styles */
.toast-container {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 400px;
}

.toast {
  transform: translateY(-100%);
  opacity: 0;
  transition: all 0.3s ease-out;
}

.toast-show {
  transform: translateY(0);
  opacity: 1;
}

.success-toast {
  background-color: #036D19;
  border-radius: 4px;
  padding: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.toast-icon {
  color: var(--background-table);
  flex-shrink: 0;
}

.toast-message {
  color: var(--background-table);
  font-weight: 500;
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .toast-container {
    left: 1rem;
    right: 1rem;
    bottom: 2rem;
    top: auto;
    transform: none;
    max-width: none;
    width: auto;
    padding: 0;
    flex-direction: column-reverse;
  }
  
  .toast {
    transform: translateY(100%);
  }
  
  .toast-show {
    transform: translateY(0);
  }
  
  .success-toast {
    box-sizing: border-box;
  }
}
</style>